import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  Article as ContentIcon,
  Language as SiteNameIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import AdminLayout from '../../components/templates/layout/AdminLayout';
import api from '../../services/api';
import ModernToast from '../../components/molecules/ModernToast/ModernToast';
import { API_CONFIG } from '../../config/api';

const FIELD_ICONS = {
  title: <TitleIcon />,
  description: <DescriptionIcon />,
  image: <ImageIcon />,
  author: <PersonIcon />,
  date: <DateIcon />,
  content: <ContentIcon />,
  siteName: <SiteNameIcon />
};

const FIELD_LABELS = {
  title: 'Título',
  description: 'Descrição',
  image: 'Imagem',
  author: 'Autor',
  date: 'Data',
  content: 'Conteúdo',
  siteName: 'Nome do Site'
};

// Seletores recomendados por campo (baseados nos mais utilizados)
const RECOMMENDED_SELECTORS = {
  title: [
    { selector: 'meta[property="og:title"]', attribute: 'content', description: 'Open Graph title (mais confiável)' },
    { selector: 'meta[name="twitter:title"]', attribute: 'content', description: 'Twitter Card title' },
    { selector: 'h1', description: 'Primeiro H1 da página' },
    { selector: 'article h1', description: 'H1 dentro de article' },
    { selector: '.entry-title', description: 'WordPress entry title' },
    { selector: '.post-title', description: 'Post title genérico' },
    { selector: '.article-title', description: 'Article title' },
    { selector: '.headline', description: 'Headline' },
  ],
  description: [
    { selector: 'meta[name="description"]', attribute: 'content', description: 'Meta description' },
    { selector: 'meta[property="og:description"]', attribute: 'content', description: 'Open Graph description' },
    { selector: 'meta[name="twitter:description"]', attribute: 'content', description: 'Twitter description' },
    { selector: '.article-description', description: 'Article description' },
    { selector: '.excerpt', description: 'Excerpt/resumo' },
    { selector: '.summary', description: 'Summary' },
    { selector: 'p:first-of-type', description: 'Primeiro parágrafo' },
  ],
  image: [
    { selector: 'meta[property="og:image"]', attribute: 'content', description: 'Open Graph image (recomendado)' },
    { selector: 'meta[name="twitter:image"]', attribute: 'content', description: 'Twitter Card image' },
    { selector: '[style*="background-image"]', attribute: 'style', description: 'CSS background-image (novo!)' },
    { selector: 'article figure img', attribute: 'src', description: 'Imagem em figure dentro de article' },
    { selector: '.featured-image img', attribute: 'src', description: 'Featured image' },
    { selector: '.post-thumbnail img', attribute: 'src', description: 'Post thumbnail' },
    { selector: '.wp-post-image', attribute: 'src', description: 'WordPress featured image' },
    { selector: 'article img', attribute: 'src', description: 'Primeira imagem do article' },
    { selector: '.entry-content img', attribute: 'src', description: 'Imagem no conteúdo' },
  ],
  author: [
    { selector: 'meta[name="author"]', attribute: 'content', description: 'Meta author' },
    { selector: 'meta[property="article:author"]', attribute: 'content', description: 'Article author' },
    { selector: '.author-name', description: 'Author name' },
    { selector: '.author', description: 'Classe author genérica' },
    { selector: '.byline', description: 'Byline' },
    { selector: '[itemprop="author"]', description: 'Schema.org author' },
    { selector: '[rel="author"]', description: 'Rel author' },
  ],
  date: [
    { selector: 'meta[property="article:published_time"]', attribute: 'content', description: 'Article published time' },
    { selector: 'time[datetime]', attribute: 'datetime', description: 'Time element com datetime' },
    { selector: 'time', description: 'Time element (texto)' },
    { selector: '.entry-date', description: 'Entry date (WordPress)' },
    { selector: '.published-date', description: 'Published date' },
    { selector: '.date', description: 'Classe date genérica' },
    { selector: '[itemprop="datePublished"]', attribute: 'content', description: 'Schema.org datePublished' },
    { selector: '.article-date', description: 'Article date' },
  ],
  content: [
    { selector: 'article', description: 'Tag article (semântico)' },
    { selector: '.entry-content', description: 'WordPress entry content' },
    { selector: '.post-content', description: 'Post content' },
    { selector: '.article-content', description: 'Article content' },
    { selector: '.article__body', description: 'Article body' },
    { selector: '[role="main"]', description: 'Main content (ARIA)' },
    { selector: 'main', description: 'Tag main' },
    { selector: '.content', description: 'Classe content genérica' },
  ],
  siteName: [
    { selector: 'meta[property="og:site_name"]', attribute: 'content', description: 'Open Graph site name (recomendado)' },
    { selector: 'meta[name="application-name"]', attribute: 'content', description: 'Application name' },
    { selector: '.site-title', description: 'Site title' },
    { selector: '.logo', description: 'Logo (texto)' },
    { selector: '.brand', description: 'Brand' },
    { selector: '.navbar-brand', description: 'Bootstrap navbar brand' },
    { selector: 'header .logo a', description: 'Link do logo no header' },
  ]
};

const ScraperConfigPage = () => {
  const theme = useTheme();
  const iframeRef = useRef(null);

  // Estados principais
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [htmlLoaded, setHtmlLoaded] = useState(false);
  const [iframeReady, setIframeReady] = useState(false); // iframe carregou e script injetado
  const [activeTab, setActiveTab] = useState(0);
  const [selectedField, setSelectedField] = useState('image');
  const [selectionMode, setSelectionMode] = useState('include'); // 'include' or 'exclude'
  const [captureMode, setCaptureMode] = useState(true); // true = capturando seletores, false = interagindo livremente

  // Configuração do site
  const [siteConfig, setSiteConfig] = useState({
    domain: '',
    name: '',
    selectors: {
      title: [],
      description: [],
      image: [],
      author: [],
      date: [],
      content: [],
      siteName: []
    },
    settings: {
      enableJavaScript: true,
      waitTime: 1000,
      scrollToBottom: true,
      readingMode: true,
      aggressiveCleaning: true,
      customCleaningSelectors: [],
      excludeImagePatterns: [] // URLs de imagens para excluir
    }
  });

  // URLs de imagem para excluir (capturadas ao clicar em imagens no modo excluir)
  const [excludeImageUrls, setExcludeImageUrls] = useState([]);

  // Seletores selecionados para cada campo
  const [selectedSelectors, setSelectedSelectors] = useState({
    title: { include: [], exclude: [] },
    description: { include: [], exclude: [] },
    image: { include: [], exclude: [] },
    author: { include: [], exclude: [] },
    date: { include: [], exclude: [] },
    content: { include: [], exclude: [] },
    siteName: { include: [], exclude: [] }
  });

  // Resultado do teste
  const [testResults, setTestResults] = useState(null);

  // Configurações existentes
  const [existingConfigs, setExistingConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');

  // Dialog para adicionar seletor manual
  const [manualSelectorDialog, setManualSelectorDialog] = useState(false);
  const [manualSelector, setManualSelector] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);

  // Carregar configurações existentes
  useEffect(() => {
    loadExistingConfigs();
  }, []);

  const loadExistingConfigs = async () => {
    try {
      const response = await api.get('/scraper/config/site');
      setExistingConfigs(response.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  };

  // Extrair domínio da URL
  const extractDomain = (urlString) => {
    try {
      const parsed = new URL(urlString);
      return parsed.hostname.replace('www.', '');
    } catch {
      return '';
    }
  };

  // Carregar página no iframe
  const loadPage = async () => {
    if (!url) {
      setError('Digite uma URL válida');
      return;
    }

    setLoading(true);
    setError('');
    setHtmlLoaded(false);
    setIframeReady(false);
    setCaptureMode(true); // Resetar para modo de captura ao carregar nova página

    try {
      // Verificar se já existe config para este domínio
      const domain = extractDomain(url);
      console.log('[ScraperConfig] Domínio extraído:', domain);
      setSiteConfig(prev => ({ ...prev, domain, name: domain }));

      // Verificar configuração existente
      try {
        const configResponse = await api.get(`/scraper/config/by-domain/${domain}`);
        if (configResponse.data.success && configResponse.data.type === 'site-specific') {
          const existingConfig = configResponse.data.data;
          setSiteConfig(existingConfig);
          setSelectedConfigId(existingConfig._id);
          setSuccess('Configuração existente carregada para este domínio');

          // Converter seletores existentes para o formato de trabalho
          const newSelectedSelectors = { ...selectedSelectors };
          Object.keys(existingConfig.selectors || {}).forEach(field => {
            if (existingConfig.selectors[field]) {
              newSelectedSelectors[field] = {
                include: existingConfig.selectors[field]
                  .filter(s => s.isActive)
                  .map(s => s.selector),
                exclude: existingConfig.selectors[field]
                  .filter(s => !s.isActive)
                  .map(s => s.selector)
              };
            }
          });
          setSelectedSelectors(newSelectedSelectors);

          // Carregar URLs de imagem excluídas existentes
          if (existingConfig.settings?.excludeImagePatterns?.length > 0) {
            setExcludeImageUrls(existingConfig.settings.excludeImagePatterns);
            console.log('[ScraperConfig] URLs de imagem excluídas carregadas:', existingConfig.settings.excludeImagePatterns);
          } else {
            setExcludeImageUrls([]);
          }
        }
      } catch (configErr) {
        console.log('[ScraperConfig] Sem configuração existente para', domain);
      }

      // Carregar HTML via proxy - usar URL absoluta do backend
      // Nota: API_CONFIG.BASE_URL = http://localhost:5000/api, então /proxy está em /api/proxy
      const proxyUrl = `${API_CONFIG.BASE_URL}/proxy/fetch-html?url=${encodeURIComponent(url)}`;
      console.log('[ScraperConfig] Carregando via proxy:', proxyUrl);

      if (iframeRef.current) {
        iframeRef.current.src = proxyUrl;
        setHtmlLoaded(true);
      } else {
        console.error('[ScraperConfig] iframeRef não está disponível');
        setError('Erro interno: iframe não disponível');
      }

    } catch (err) {
      console.error('[ScraperConfig] Erro ao carregar página:', err);
      setError(`Erro ao carregar página: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Listener para mensagens do iframe (cliques e ready)
  useEffect(() => {
    const handleMessage = (event) => {
      // Ignorar mensagens de outras origens se necessário
      if (!event.data || !event.data.type) return;

      if (event.data.type === 'selector-builder-ready') {
        console.log('[ScraperConfig] Script do iframe pronto!');
        setIframeReady(true);
        setSuccess('Página carregada! Clique nos elementos para capturar seletores.');
      } else if (event.data.type === 'element-clicked') {
        handleElementClick(event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [selectedField, selectionMode]);

  // Sincronizar captureMode com o iframe via postMessage
  useEffect(() => {
    if (iframeReady && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'toggle-capture', enabled: captureMode },
        '*'
      );
    }
  }, [captureMode, iframeReady]);

  // Quando o iframe carrega (o script já foi injetado pelo servidor)
  const handleIframeLoad = () => {
    console.log('[ScraperConfig] Iframe carregado, aguardando script do servidor...');
    // O script é injetado pelo servidor no proxy, então apenas logamos aqui
    // O iframeReady será setado quando recebermos a mensagem 'selector-builder-ready'
  };

  // Handler para erro no iframe
  const handleIframeError = () => {
    console.error('[ScraperConfig] Erro ao carregar iframe');
    setError('Erro ao carregar a página. Verifique se a URL é válida e acessível.');
    setHtmlLoaded(false);
    setIframeReady(false);
  };

  // Processar clique em elemento
  const handleElementClick = (data) => {
    console.log('[ScraperConfig] handleElementClick chamado:', data);
    console.log('[ScraperConfig] Campo atual:', selectedField, '| Modo:', selectionMode);

    // NOVO: Se estiver excluindo uma imagem, capturar a URL para exclusão direta
    if (selectedField === 'image' && selectionMode === 'exclude' && data.src) {
      console.log('[ScraperConfig] Capturando URL de imagem para exclusão:', data.src);

      // Extrair parte identificadora da URL (últimas partes do path ou nome do arquivo)
      let imagePattern = '';
      try {
        const urlObj = new URL(data.src);
        const pathParts = urlObj.pathname.split('/').filter(p => p);
        // Pegar as últimas 2 partes do path ou o nome do arquivo
        if (pathParts.length >= 2) {
          imagePattern = pathParts.slice(-2).join('/');
        } else if (pathParts.length === 1) {
          imagePattern = pathParts[0];
        } else {
          imagePattern = urlObj.hostname;
        }
      } catch (e) {
        // Se não for URL válida, usar parte do src
        const srcParts = data.src.split('/').filter(p => p);
        imagePattern = srcParts.slice(-2).join('/') || data.src.substring(0, 50);
      }

      // Adicionar à lista de URLs excluídas (se ainda não existir)
      setExcludeImageUrls(prev => {
        if (!prev.includes(imagePattern)) {
          console.log('[ScraperConfig] Padrão de imagem adicionado para exclusão:', imagePattern);
          return [...prev, imagePattern];
        }
        return prev;
      });

      setSuccess(`Imagem "${imagePattern}" adicionada para exclusão. Será filtrada do scraping.`);
      return; // Não precisa adicionar seletor CSS para imagens excluídas
    }

    // Gerar seletor baseado nos dados do elemento
    let selector = '';

    if (data.id) {
      selector = `#${data.id}`;
    } else if (data.className && typeof data.className === 'string') {
      const classes = data.className.split(' ').filter(c => c.trim());
      if (classes.length > 0) {
        selector = `.${classes[0]}`;
        if (data.tagName) {
          selector = `${data.tagName.toLowerCase()}${selector}`;
        }
      }
    } else if (data.path) {
      selector = data.path;
    } else {
      selector = data.tagName?.toLowerCase() || 'div';
    }

    // Adicionar seletor à lista apropriada
    setSelectedSelectors(prev => {
      const fieldSelectors = { ...prev[selectedField] };
      const listKey = selectionMode;

      if (!fieldSelectors[listKey].includes(selector)) {
        fieldSelectors[listKey] = [...fieldSelectors[listKey], selector];
      }

      return { ...prev, [selectedField]: fieldSelectors };
    });

    console.log('[ScraperConfig] Seletor gerado:', selector);
    setSuccess(`Seletor "${selector}" adicionado para ${FIELD_LABELS[selectedField]} (${selectionMode === 'include' ? 'incluir' : 'excluir'})`);
  };

  // Remover seletor
  const removeSelector = (field, type, selector) => {
    setSelectedSelectors(prev => {
      const fieldSelectors = { ...prev[field] };
      fieldSelectors[type] = fieldSelectors[type].filter(s => s !== selector);
      return { ...prev, [field]: fieldSelectors };
    });
  };

  // Adicionar seletor manual
  const addManualSelector = () => {
    if (!manualSelector.trim()) return;

    setSelectedSelectors(prev => {
      const fieldSelectors = { ...prev[selectedField] };
      if (!fieldSelectors[selectionMode].includes(manualSelector)) {
        fieldSelectors[selectionMode] = [...fieldSelectors[selectionMode], manualSelector];
      }
      return { ...prev, [selectedField]: fieldSelectors };
    });

    setManualSelector('');
    setManualSelectorDialog(false);
  };

  // Helper: buscar attribute do seletor recomendado
  const getAttributeForSelector = (field, selector) => {
    const recommended = RECOMMENDED_SELECTORS[field]?.find(r => r.selector === selector);
    return recommended?.attribute || null;
  };

  // Helper: converter seletores para formato da API (com attribute)
  const convertSelectorsForAPI = () => {
    const selectors = {};
    Object.keys(selectedSelectors).forEach(field => {
      const includeSelectors = selectedSelectors[field].include.map((selector, index) => {
        const attribute = getAttributeForSelector(field, selector);
        return {
          name: `${field}_include_${index}`,
          type: 'css',
          selector,
          attribute, // null se não tiver, ou o attribute do seletor recomendado
          priority: index + 1,
          isActive: true
        };
      });

      const excludeSelectors = selectedSelectors[field].exclude.map((selector, index) => ({
        name: `${field}_exclude_${index}`,
        type: 'css',
        selector,
        priority: 100 + index,
        isActive: false,
        filterUnwanted: true
      }));

      selectors[field] = [...includeSelectors, ...excludeSelectors];
    });
    return selectors;
  };

  // Testar configuração
  const testConfiguration = async () => {
    if (!url) {
      setError('Carregue uma URL primeiro');
      return;
    }

    setLoading(true);
    setTestResults(null);

    try {
      const selectors = convertSelectorsForAPI();

      const response = await api.post('/scraper/config/test', {
        url,
        selectors
      });

      setTestResults(response.data.data);
      setSuccess('Teste concluído!');

    } catch (err) {
      setError(`Erro no teste: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Salvar configuração
  const saveConfiguration = async () => {
    if (!siteConfig.domain) {
      setError('Carregue uma URL primeiro');
      return;
    }

    setLoading(true);

    try {
      // Usar helper que já inclui attribute quando disponível
      const selectors = convertSelectorsForAPI();

      // Remover campos vazios
      Object.keys(selectors).forEach(field => {
        if (selectors[field].length === 0) {
          delete selectors[field];
        }
      });

      // Incluir URLs de imagem excluídas no settings
      const settingsWithExcludes = {
        ...siteConfig.settings,
        excludeImagePatterns: excludeImageUrls
      };

      const configData = {
        domain: siteConfig.domain,
        name: siteConfig.name || siteConfig.domain,
        selectors,
        settings: settingsWithExcludes
      };

      let response;
      if (selectedConfigId) {
        // Atualizar existente
        response = await api.put(`/scraper/config/site/${selectedConfigId}`, configData);
      } else {
        // Criar nova
        response = await api.post('/scraper/config/site', configData);
        setSelectedConfigId(response.data.data._id);
      }

      setSuccess('Configuração salva com sucesso!');
      loadExistingConfigs();

    } catch (err) {
      setError(`Erro ao salvar: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Excluir configuração
  const deleteConfiguration = async () => {
    if (!selectedConfigId) return;

    setLoading(true);
    setDeleteConfirmDialog(false);
    try {
      await api.delete(`/scraper/config/site/${selectedConfigId}`);
      setSuccess('Configuração excluída com sucesso!');
      
      // Limpar formulário
      setSiteConfig({
        domain: '',
        name: '',
        selectors: {
          title: [], description: [], image: [], author: [], date: [], content: [], siteName: []
        },
        settings: {
          enableJavaScript: true, waitTime: 1000, scrollToBottom: true, readingMode: true, aggressiveCleaning: true, customCleaningSelectors: [], excludeImagePatterns: []
        }
      });
      setSelectedSelectors({
        title: { include: [], exclude: [] }, description: { include: [], exclude: [] }, image: { include: [], exclude: [] }, author: { include: [], exclude: [] }, date: { include: [], exclude: [] }, content: { include: [], exclude: [] }, siteName: { include: [], exclude: [] }
      });
      setExcludeImageUrls([]);
      setSelectedConfigId('');
      setUrl('');
      setHtmlLoaded(false);
      setIframeReady(false);
      
      loadExistingConfigs();
    } catch (err) {
      setError(`Erro ao excluir: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Configurador Visual de Scraper
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Carregue uma URL e clique nos elementos para configurar os seletores de extração.
        </Typography>

        {/* Barra de URL */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="URL do artigo"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://exemplo.com/artigo"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={loadPage}
                disabled={loading || !url}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                Carregar
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Config. Existente</InputLabel>
                <Select
                  value={selectedConfigId}
                  onChange={(e) => {
                    const config = existingConfigs.find(c => c._id === e.target.value);
                    if (config) {
                      setSiteConfig(config);
                      setSelectedConfigId(config._id);
                    }
                  }}
                  label="Config. Existente"
                >
                  <MenuItem value="">Nova configuração</MenuItem>
                  {existingConfigs.map(config => (
                    <MenuItem key={config._id} value={config._id}>
                      {config.domain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Painel Esquerdo - Preview */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ height: 'calc(100vh - 300px)', overflow: 'hidden' }}>
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Preview do Site</Typography>
                {iframeReady && (
                  <Chip
                    size="small"
                    label={`Modo: ${selectionMode === 'include' ? 'INCLUIR' : 'EXCLUIR'}`}
                    color={selectionMode === 'include' ? 'success' : 'error'}
                  />
                )}
                {iframeReady && (
                  <Tooltip title={captureMode
                    ? 'Pausar captura — permite interagir com a página normalmente (fechar popups, rolar, etc.)'
                    : 'Retomar captura de seletores'
                  }>
                    <Chip
                      size="small"
                      label={captureMode ? '⏸ Pausar captura' : '▶ Retomar captura'}
                      color={captureMode ? 'primary' : 'warning'}
                      onClick={() => setCaptureMode(prev => !prev)}
                      sx={{ cursor: 'pointer', ml: 'auto' }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ height: 'calc(100% - 40px)', bgcolor: 'grey.100', position: 'relative' }}>
                {/* Iframe sempre renderizado para o ref estar disponível */}
                <iframe
                  ref={iframeRef}
                  title="Site Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: htmlLoaded ? 'block' : 'none',
                    opacity: iframeReady ? 1 : 0.3,
                    backgroundColor: '#ffffff'
                  }}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                  sandbox="allow-same-origin allow-scripts"
                />

                {/* Loading indicator */}
                {htmlLoaded && !iframeReady && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Carregando página...
                    </Typography>
                  </Box>
                )}

                {/* Placeholder quando não há URL */}
                {!htmlLoaded && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">
                      Digite uma URL e clique em "Carregar" para visualizar o site
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Painel Direito - Configuração */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ height: 'calc(100vh - 300px)', display: 'flex', flexDirection: 'column' }}>
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Seletores" />
                <Tab label="Configurações" />
                <Tab label="Teste" />
              </Tabs>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {/* Tab: Seletores */}
                {activeTab === 0 && (
                  <Box>
                    {/* Seleção do campo */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Campo a configurar</InputLabel>
                      <Select
                        value={selectedField}
                        onChange={(e) => setSelectedField(e.target.value)}
                        label="Campo a configurar"
                      >
                        {Object.keys(FIELD_LABELS).map(field => (
                          <MenuItem key={field} value={field}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {FIELD_ICONS[field]}
                              {FIELD_LABELS[field]}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Modo de seleção */}
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                      <Button
                        variant={selectionMode === 'include' ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => setSelectionMode('include')}
                        startIcon={<CheckIcon />}
                        fullWidth
                      >
                        Incluir
                      </Button>
                      <Button
                        variant={selectionMode === 'exclude' ? 'contained' : 'outlined'}
                        color="error"
                        onClick={() => setSelectionMode('exclude')}
                        startIcon={<CancelIcon />}
                        fullWidth
                      >
                        Excluir
                      </Button>
                    </Box>

                    {/* Lista de seletores */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Seletores para {FIELD_LABELS[selectedField]}:
                    </Typography>

                    {/* Incluídos */}
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                      Incluir:
                    </Typography>
                    <List dense>
                      {selectedSelectors[selectedField]?.include.map((selector, index) => (
                        <ListItem key={`inc-${index}`} sx={{ py: 0 }}>
                          <ListItemText
                            primary={selector}
                            primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => removeSelector(selectedField, 'include', selector)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                      {selectedSelectors[selectedField]?.include.length === 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Nenhum seletor de inclusão"
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontStyle: 'italic' }}
                          />
                        </ListItem>
                      )}
                    </List>

                    <Divider sx={{ my: 1 }} />

                    {/* Excluídos */}
                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                      Excluir:
                    </Typography>
                    <List dense>
                      {selectedSelectors[selectedField]?.exclude.map((selector, index) => (
                        <ListItem key={`exc-${index}`} sx={{ py: 0 }}>
                          <ListItemText
                            primary={selector}
                            primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => removeSelector(selectedField, 'exclude', selector)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                      {selectedSelectors[selectedField]?.exclude.length === 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Nenhum seletor de exclusão"
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontStyle: 'italic' }}
                          />
                        </ListItem>
                      )}
                    </List>

                    {/* URLs de Imagem Excluídas (apenas para campo imagem) */}
                    {selectedField === 'image' && excludeImageUrls.length > 0 && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                          URLs de Imagem Excluídas ({excludeImageUrls.length}):
                        </Typography>
                        <List dense>
                          {excludeImageUrls.map((pattern, index) => (
                            <ListItem key={`img-exc-${index}`} sx={{ py: 0 }}>
                              <ListItemText
                                primary={pattern}
                                primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace', fontSize: '0.75rem' }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  size="small"
                                  onClick={() => setExcludeImageUrls(prev => prev.filter(p => p !== pattern))}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setManualSelectorDialog(true)}
                      sx={{ mt: 1 }}
                    >
                      Adicionar manualmente
                    </Button>

                    {/* Seletores Recomendados */}
                    <Accordion sx={{ mt: 2 }} defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarIcon fontSize="small" color="warning" />
                          <Typography variant="subtitle2">
                            Seletores Recomendados para {FIELD_LABELS[selectedField]}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                          {RECOMMENDED_SELECTORS[selectedField]?.map((item, index) => {
                            const isAlreadyAdded =
                              selectedSelectors[selectedField]?.include.includes(item.selector) ||
                              selectedSelectors[selectedField]?.exclude.includes(item.selector);

                            return (
                              <ListItemButton
                                key={index}
                                onClick={() => {
                                  if (!isAlreadyAdded) {
                                    setSelectedSelectors(prev => {
                                      const fieldSelectors = { ...prev[selectedField] };
                                      fieldSelectors[selectionMode] = [...fieldSelectors[selectionMode], item.selector];
                                      return { ...prev, [selectedField]: fieldSelectors };
                                    });
                                    setSuccess(`Seletor "${item.selector}" adicionado para ${selectionMode === 'include' ? 'incluir' : 'excluir'}`);
                                  }
                                }}
                                disabled={isAlreadyAdded}
                                sx={{
                                  py: 0.5,
                                  opacity: isAlreadyAdded ? 0.5 : 1,
                                  '&:hover': { bgcolor: selectionMode === 'include' ? 'success.light' : 'error.light' }
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                      >
                                        {item.selector}
                                      </Typography>
                                      {item.attribute && (
                                        <Chip
                                          label={`attr: ${item.attribute}`}
                                          size="small"
                                          sx={{ height: 16, fontSize: '0.65rem' }}
                                        />
                                      )}
                                      {isAlreadyAdded && (
                                        <Chip
                                          label="já adicionado"
                                          size="small"
                                          color="default"
                                          sx={{ height: 16, fontSize: '0.65rem' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={item.description}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}

                {/* Tab: Configurações */}
                {activeTab === 1 && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Domínio"
                      value={siteConfig.domain}
                      onChange={(e) => setSiteConfig(prev => ({ ...prev, domain: e.target.value }))}
                      sx={{ mb: 2 }}
                      disabled
                    />
                    <TextField
                      fullWidth
                      label="Nome do Site"
                      value={siteConfig.name}
                      onChange={(e) => setSiteConfig(prev => ({ ...prev, name: e.target.value }))}
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={siteConfig.settings?.enableJavaScript ?? true}
                          onChange={(e) => setSiteConfig(prev => ({
                            ...prev,
                            settings: { ...prev.settings, enableJavaScript: e.target.checked }
                          }))}
                        />
                      }
                      label="Executar JavaScript"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={siteConfig.settings?.scrollToBottom ?? true}
                          onChange={(e) => setSiteConfig(prev => ({
                            ...prev,
                            settings: { ...prev.settings, scrollToBottom: e.target.checked }
                          }))}
                        />
                      }
                      label="Scroll até o final"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={siteConfig.settings?.aggressiveCleaning ?? true}
                          onChange={(e) => setSiteConfig(prev => ({
                            ...prev,
                            settings: { ...prev.settings, aggressiveCleaning: e.target.checked }
                          }))}
                        />
                      }
                      label="Limpeza agressiva"
                    />
                    <TextField
                      fullWidth
                      label="Tempo de espera (ms)"
                      type="number"
                      value={siteConfig.settings?.waitTime ?? 1000}
                      onChange={(e) => setSiteConfig(prev => ({
                        ...prev,
                        settings: { ...prev.settings, waitTime: parseInt(e.target.value) || 1000 }
                      }))}
                      sx={{ mt: 2 }}
                    />
                  </Box>
                )}

                {/* Tab: Teste */}
                {activeTab === 2 && (
                  <Box>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={testConfiguration}
                      disabled={loading || !url}
                      startIcon={loading ? <CircularProgress size={20} /> : <TestIcon />}
                      sx={{ mb: 2 }}
                    >
                      Testar Seletores
                    </Button>

                    {testResults && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Resultados do Teste:
                        </Typography>
                        {Object.entries(testResults.results || {}).map(([field, result]) => (
                          <Card key={field} sx={{ mb: 1 }}>
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {FIELD_ICONS[field]}
                                <Typography variant="body2" fontWeight="medium">
                                  {FIELD_LABELS[field] || field}
                                </Typography>
                                {result.found ? (
                                  <CheckIcon color="success" fontSize="small" />
                                ) : (
                                  <CancelIcon color="error" fontSize="small" />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {typeof result.value === 'string'
                                  ? result.value.substring(0, 100) + (result.value.length > 100 ? '...' : '')
                                  : JSON.stringify(result.value)?.substring(0, 100)
                                }
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Botões de ação */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
                {selectedConfigId && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteConfirmDialog(true)}
                    disabled={loading}
                    startIcon={<DeleteIcon />}
                  >
                    Excluir
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={saveConfiguration}
                  disabled={loading || !siteConfig.domain}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {selectedConfigId ? 'Atualizar' : 'Salvar'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Dialog para seletor manual */}
        <Dialog open={manualSelectorDialog} onClose={() => setManualSelectorDialog(false)}>
          <DialogTitle>Adicionar Seletor Manualmente</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Seletor CSS"
              value={manualSelector}
              onChange={(e) => setManualSelector(e.target.value)}
              placeholder="Ex: .article-image img, #main-content"
              sx={{ mt: 1 }}
              helperText="Digite um seletor CSS válido"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualSelectorDialog(false)}>Cancelar</Button>
            <Button onClick={addManualSelector} variant="contained">Adicionar</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog open={deleteConfirmDialog} onClose={() => setDeleteConfirmDialog(false)}>
          <DialogTitle>Excluir Configuração</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir esta configuração? O site voltará a usar os métodos padrão de scraping e todos os seletores personalizados mapeados serão perdidos.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmDialog(false)}>Cancelar</Button>
            <Button onClick={deleteConfiguration} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar de sucesso */}
        <ModernToast
          open={!!success}
          message={success}
          severity="success"
          onClose={() => setSuccess('')}
          duration={3000}
          position="bottom-center"
        />
      </Box>
    </AdminLayout>
  );
};

export default ScraperConfigPage;
